// create a typescript react functional component with the name Table that has an export for itself, table rows, table cells, table body and table head. Ensure that the table uses tailwind CSS classes to format as modern table with dark mode support. The table should take a prop that has the table data including headings. On mobile display the table should be a responsive linear layout
//
// For example, the component should be used as follows:
//   <Table data={data} callback={callbackFunc} isSellectAllEnabled={true} />
//
// where data is an array of objects with the following structure:
//
//     {
//       headings: [{ title: 'Name', attribute: 'name'}, { title: 'Age', attribute: 'age'}],
//       rows: [
//         { name: 'John Doe', type: 'td', age: 25 },
//         { name: 'Adam Doe', type: 'td', age: 24 },
//       ],
//     }
// The component should be able to include a checkbox control in the table cells with a call back function. Any example of the structure of the data is as follows:
//
//     {
//       data: {
//          headings: [{ title: 'Name', attribute: 'name'}, { title: 'Age', attribute: 'age'}],
//          caption: 'Employees table showing their name and age',
//          rows: [
//            { checkbox: 'none', checked: false, type: 'th', name: 'Named employees' },
//            { checkbox: 'enabled', checked: true, type: 'td', name: 'John Doe', age: 25 },
//            { checkbox: 'enabled', checked: false, type: 'td', name: 'Adam Doe', age: 24 },
//            { checkbox: 'disabled' checked: false, type: 'td', name: 'Simon Doe', age: 18 },
//          ],
//       },
//       callback: (row, checked) => {},
//       isSelectAllEnabled: true,
//     }
//
// When the table data payload contains these extra attributes a checkbox column should be displayed with the state of the checkbox defined in the checkbox attribute, and the checked state defined in the checked attribute. The table component should have a callback function that is called when the checkbox is clicked. The callback function should be passed the row data and the new checked state of the checkbox. This callback function should be an optional prop of the component.
//
// The component should be defined in a file named Table.tsx under the path webappv2/src/components/AppContainer/ChatView/ChatWindow/Shared
//
// The component should use the following semantic HTML elements:
// - table
//  - caption (if needed)
//  - thead
//    - tr
//    - th (for each heading - with scope and colspan if needed)
// - tbody
//  - th (if needed)
//  - tr
//    - td (for each heading - with scope and colspan if needed)

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import React, { useEffect } from 'react';
import useScreenSize from '../../../../../libs/hooks/useScreenSize';

export interface TableRowProps {
    id: string;
    checkbox?: string | undefined;
    checked?: boolean | undefined;
    type: string;
    [key: string]: string | number | boolean | JSX.Element | undefined;
}

export interface TableProps {
    data: {
        caption?: string;
        headings: string[];
        rows: TableRowProps[];
        hidden: string[];
    };
    isSelectAllEnabled?: boolean;
    onRowsSelectionChange?: (selectedItems: TableRowProps[]) => void;
}

const applyLargeBreakpoint = (smClass: string, headingsCount: number, applyWhenCount: number = 5) => {
    const stylingMappings = {
        'sm:border-1': 'lg:border-1',
        'sm:[&:not(:first-child)]:hidden': 'lg:[&:not(:first-child)]:hidden',
        'sm:table-row': 'lg:table-row',
        'sm:rounded-none': 'lg:rounded-none',
        'sm:shadow-none': 'lg:shadow-none',
        'sm:border': 'lg:border',
        'sm:visible': 'lg:visible',
        'sm:border-0': 'lg:border-0',
        'sm:text-center': 'lg:text-center',
        'sm:whitespace-normal': 'lg:whitespace-normal',
        'sm:flex-1': 'lg:flex-1',
        'sm:hidden': 'lg:hidden',
        'sm:w-full': 'lg:w-full',
        'sm:items-center': 'lg:items-center',
        'sm:p-0': 'lg:p-0',
        'sm:my-8': 'lg:my-8',
        'sm:max-w-xl': 'lg:max-w-xl',
        'sm:translate-y-0': 'lg:translate-y-0',
        'sm:scale-95': 'lg:scale-95',
        'sm:p-6': 'lg:p-6',
        'sm:pb-4': 'lg:pb-4',
        'sm:flex': 'lg:flex',
        'sm:items-start': 'lg:items-start',
        'sm:flex-row-reverse': 'lg:flex-row-reverse',
        'sm:px-6': 'lg:px-6',
        'sm:inline-table': 'lg:inline-table',
        'sm:shadow': 'lg:shadow',
    };

    return headingsCount > applyWhenCount ? stylingMappings[smClass] : smClass;
};

export const TableHead = ({
    headings,
    isCheckBoxes = false,
    isSelectAllEnabled = false,
    rowCount = 0,
    onRowsSelectionToggleChange,
}: {
    headings: string[];
    isCheckBoxes?: boolean;
    isSelectAllEnabled?: boolean;
    rowCount?: number;
    onRowsSelectionToggleChange?: (checked: boolean) => void;
}) => {
    const headingsCount = headings.length;

    return (
        <thead className="rounded text-slate-700 dark:text-white">
            {rowCount > 0 &&
                Array.from({ length: rowCount }, (_, rowIndex) => (
                    <tr
                        key={`thead-tr-${rowIndex}`}
                        onClick={(event) => {
                            const target = event.target as HTMLElement;
                            if (
                                target.tagName.toLowerCase() !== 'input' ||
                                (target as HTMLInputElement).type !== 'checkbox'
                            ) {
                                const tr = target.closest('tr');

                                if (tr !== null) {
                                    const checkBox = tr.querySelector('input[type="checkbox"]') as HTMLInputElement;

                                    if (checkBox !== null) {
                                        const isCheckBoxChecked = !checkBox.checked;
                                        checkBox.checked = isCheckBoxChecked; // switch checkbox value

                                        if (onRowsSelectionToggleChange) {
                                            onRowsSelectionToggleChange(isCheckBoxChecked);
                                        }
                                    }
                                }
                            }
                        }}
                        className={`flex flex-col flex-no border border-gray-200 dark:border-gray-700 ${applyLargeBreakpoint('sm:border-1', headingsCount)} wrap ${applyLargeBreakpoint('sm:[&:not(:first-child)]:hidden', headingsCount)} ${applyLargeBreakpoint('sm:table-row', headingsCount)} rounded-l-lg ${applyLargeBreakpoint('sm:rounded-none', headingsCount)} bg-slate-200 dark:bg-slate-700 mb-2 shadow ${applyLargeBreakpoint('sm:shadow-none', headingsCount)}`}>
                        {isCheckBoxes && (
                            <th
                                scope="col"
                                className={`text-center p-2 ${applyLargeBreakpoint('sm:border', headingsCount)} border-gray-200 dark:border-gray-800`}>
                                {/* Need this to hide the select all checkbox on collapsed mobile view - displayed elsewhere */}
                                <div className={`invisible ${applyLargeBreakpoint('sm:visible', headingsCount)}`}>
                                    {isSelectAllEnabled ? (
                                        <input
                                            type="checkbox"
                                            className={`sm:${rowIndex !== 0 ? 'invisible' : 'visible'}`}
                                            onChange={(e) => {
                                                if (onRowsSelectionToggleChange) {
                                                    onRowsSelectionToggleChange(e.target.checked);
                                                }
                                            }}
                                        />
                                    ) : null}
                                </div>
                            </th>
                        )}
                        {headings.map((heading, index) => (
                            <TableHeadCell
                                headingsCount={headingsCount}
                                key={`th-${index}`}
                                index={index}
                                heading={heading}
                            />
                        ))}
                    </tr>
                ))}
        </thead>
    );
};

export const TableHeadCell = ({
    headingsCount,
    heading,
    index,
}: {
    headingsCount: number;
    heading: string;
    index: number;
}) => {
    return (
        <th
            scope="col"
            key={`th-${index}`}
            className={`text-left p-2 ${applyLargeBreakpoint('sm:border', headingsCount)} border-gray-200 dark:border-gray-800`}>
            {heading}
        </th>
    );
};

export const TableBodyRow = ({
    headingsCount,
    rowKey,
    row,
    hidden,
    onSelectRow,
}: {
    headingsCount: number;
    rowKey: number;
    row: TableRowProps;
    hidden: string[];
    onSelectRow?: (selectedRow: TableRowProps, isDetailDisplayCancel?: boolean) => void;
}) => {
    return (
        <tr
            className={`flex flex-col flex-no-wrap rounded-r-lg ${applyLargeBreakpoint('sm:rounded-none', headingsCount)} border ${applyLargeBreakpoint('sm:border-0', headingsCount)} overflow-hidden border-gray-200 dark:border-gray-700 bg-slate-50 hover:cursor-pointer hover:bg-slate-100 dark:bg-slate-800 hover:dark:bg-slate-900 ${applyLargeBreakpoint('sm:table-row', headingsCount)} mb-2 shadow ${applyLargeBreakpoint('sm:shadow-none', headingsCount)}`}
            onClick={(e) => {
                const target = e.target as HTMLElement;
                if (target.tagName.toLowerCase() !== 'input' || (target as HTMLInputElement).type !== 'checkbox') {
                    if (onSelectRow) {
                        if (onSelectRow) {
                            onSelectRow({ ...row, checked: !row.checked }, false);
                        }
                    }
                }
            }}>
            {row.checkbox && (
                <th
                    className={`p-2 text-left ${applyLargeBreakpoint('sm:text-center', headingsCount)} ${applyLargeBreakpoint('sm:border', headingsCount)} border-gray-200 dark:border-gray-800`}>
                    {row.checkbox === 'enabled' ? (
                        <input
                            key={`checkbox-${rowKey}`}
                            type="checkbox"
                            checked={row?.checked}
                            onChange={(e) => {
                                e.stopPropagation();
                                const newChecked = e.target.checked;

                                if (onSelectRow) {
                                    onSelectRow({ ...row, checked: newChecked }, true);
                                }
                            }}
                        />
                    ) : (
                        <input
                            type="checkbox"
                            key={`checkbox-${rowKey}`}
                            className="text-center sticky left-0 z-10"
                            disabled
                        />
                    )}
                </th>
            )}
            {Object.keys(row).map((key, index) => {
                if (hidden.indexOf(key) > -1) {
                    return;
                }

                return (
                    <td
                        key={`tr-${rowKey}-td-${index}`}
                        className={`p-2 ${applyLargeBreakpoint('sm:border', headingsCount)} border-gray-200 dark:border-gray-800 max-w-xs`}>
                        <div
                            className={`overflow-hidden text-ellipsis whitespace-nowrap ${applyLargeBreakpoint('sm:whitespace-normal', headingsCount)}`}>
                            {row[key]}
                        </div>
                    </td>
                );
            })}
        </tr>
    );
};

export const TableBody = ({
    headingsCount,
    rows,
    hidden,
    onSelectRow,
}: {
    headingsCount: number;
    rows: TableRowProps[];
    hidden: string[];
    onSelectRow?: (selectedRow: TableRowProps) => void;
}) => {
    return (
        <tbody
            className={`flex-grow ${applyLargeBreakpoint('sm:flex-1', headingsCount)} rounded-b-lg overflow-hidden md:overflow-visible`}>
            {rows.map((row, index) => (
                <TableBodyRow
                    headingsCount={headingsCount}
                    key={`tr-${index}`}
                    rowKey={index}
                    row={row}
                    hidden={hidden}
                    onSelectRow={onSelectRow}
                />
            ))}
        </tbody>
    );
};

// a simple accessible table component usign Tailwind CSS
export const Table: React.FC<TableProps> = ({ data, onRowsSelectionChange, isSelectAllEnabled }) => {
    const [rows, setRows] = React.useState(data.rows);
    const [mobileSelectedRow, setMobileSelectedRow] = React.useState<string>('');
    const [open, setOpen] = React.useState(false);
    const screenSize = useScreenSize();

    React.useEffect(() => {
        setRows(data.rows);
    }, [data.rows]);

    useEffect(() => {
        updateTdHeights();

        // Re-run the height adjustment on window resize
        window.addEventListener('resize', updateTdHeights);

        return () => {
            window.removeEventListener('resize', updateTdHeights);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenSize]);

    const updateTdHeights = () => {
        if (screenSize.width < 640) {
            document.querySelectorAll('table').forEach((table) => {
                const ths = table.querySelectorAll('thead th');
                const trs = table.querySelectorAll('tbody tr');

                trs.forEach((tr) => {
                    tr.querySelectorAll('td').forEach((td, tdIndex) => {
                        // Cast the th element to HTMLTableCellElement
                        const thElement = ths[tdIndex] as HTMLTableCellElement;
                        if (thElement) {
                            const thHeight = thElement.offsetHeight;
                            td.style.height = `${thHeight}px`;
                        }
                    });
                });
            });
        } else {
            // Reset td heights if needed
            document.querySelectorAll('tbody td').forEach((td) => {
                (td as HTMLElement).style.height = 'auto';
            });
        }
    };

    //isDetailDisplayCancel allows us to stop the detail display for mobile when the checkbox within the row is clicked...
    const onSelectRow = (row: TableRowProps, isDetailDisplayCancel?: boolean) => {
        const updatedRows = rows.map((r) => (r.id === row.id ? { ...r, checked: !r.checked } : r));

        if (onRowsSelectionChange) {
            onRowsSelectionChange(updatedRows);
        }
        setRows(updatedRows);

        if (screenSize.width < 640 && !isDetailDisplayCancel) {
            setMobileSelectedRow(row.id);
            setOpen(true);
        }
    };

    const onRowsSelectionToggleChange = (checked: boolean) => {
        const updatedRows = rows.map((r) => ({ ...r, checked: checked }));

        if (onRowsSelectionChange) {
            onRowsSelectionChange(updatedRows);
        }
        setRows(updatedRows);
    };

    const getDialogContent = () => {
        if (mobileSelectedRow) {
            return Object.keys(rows.filter((r) => r.id === mobileSelectedRow)[0])
                .filter((key) => data.hidden.indexOf(key) === -1)
                .map((key, index) => {
                    return (
                        <div key={`tr-td-${index}`} className="p-1.5">
                            <div className="font-bold">{data.headings[index]}</div>
                            <p>{rows.filter((r) => r.id === mobileSelectedRow)[0][key]}</p>
                        </div>
                    );
                });
        }
        return '';
    };

    const changeMobileSelectedRow = (direction: 'next' | 'previous') => {
        const currentIndex = rows.findIndex((r) => r.id === mobileSelectedRow);

        if (direction === 'next') {
            if (currentIndex < rows.length - 1) {
                setMobileSelectedRow(rows[currentIndex + 1].id);
            }
        } else {
            if (currentIndex > 0) {
                setMobileSelectedRow(rows[currentIndex - 1].id);
            }
        }
    };

    const isPreviousDisabled = rows.findIndex((r) => r.id === mobileSelectedRow) === 0;
    const isNextDisabled = rows.findIndex((r) => r.id === mobileSelectedRow) === rows.length - 1;

    const headingsCount = data.headings.length;

    // add a table element using Tailwind CSS classes that renders as a linear coloumn on mobile, but as a normal table on desktop
    return (
        <>
            {isSelectAllEnabled ? (
                <label
                    className={`block ${applyLargeBreakpoint('sm:hidden', headingsCount)} w-full ${applyLargeBreakpoint('sm:w-full', headingsCount)} flex p-2 mt-3 hyphens-auto items-center justify-center bg-slate-50 hover:cursor-pointer hover:bg-slate-100 dark:bg-slate-800 hover:dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg`}>
                    <input
                        type="checkbox"
                        className="mr-2"
                        onChange={(e) => onRowsSelectionToggleChange && onRowsSelectionToggleChange(e.target.checked)}
                    />{' '}
                    Select all rows
                </label>
            ) : null}

            <Dialog open={open} onClose={setOpen} className="relative z-50 text-sm text-slate-700 dark:text-slate-200">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div
                        className={`flex min-h-full items-center justify-center p-4 text-center ${applyLargeBreakpoint('sm:items-center', headingsCount)} ${applyLargeBreakpoint('sm:p-0', headingsCount)}`}>
                        <DialogPanel
                            transition
                            className={`relative w-11/12 full transform overflow-hidden rounded-lg bg-slate-50 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in ${applyLargeBreakpoint('sm:my-8', headingsCount)} ${applyLargeBreakpoint('sm:w-full', headingsCount)} ${applyLargeBreakpoint('sm:max-w-xl', headingsCount)} data-[closed]:${applyLargeBreakpoint('sm:translate-y-0', headingsCount)} data-[closed]:${applyLargeBreakpoint('sm:scale-95', headingsCount)} dark:bg-slate-700`}>
                            <div
                                className={`bg-slate-50 px-4 pb-4 pt-5 ${applyLargeBreakpoint('sm:p-6', headingsCount)} ${applyLargeBreakpoint('sm:pb-4', headingsCount)} dark:bg-slate-700`}>
                                <div
                                    className={`${applyLargeBreakpoint('sm:flex', headingsCount)} ${applyLargeBreakpoint('sm:items-start', headingsCount)}`}>
                                    <div className="text-left">
                                        <DialogTitle as="h1" className="text-2xl mb-3 flex justify-end">
                                            <button onClick={() => setOpen(false)}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                    className="size-6">
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </button>
                                        </DialogTitle>
                                        <div>{getDialogContent()}</div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={`bg-gray-50 px-5 py-3 ${applyLargeBreakpoint('sm:flex', headingsCount)} ${applyLargeBreakpoint('sm:flex-row-reverse', headingsCount)} ${applyLargeBreakpoint('sm:px-6', headingsCount)} dark:bg-slate-700 text-right items-start flex flex-row justify-center`}>
                                <button
                                    type="button"
                                    data-autofocus
                                    title={`${isPreviousDisabled ? '' : 'Previous row'}`}
                                    onClick={() => changeMobileSelectedRow('previous')}
                                    disabled={isPreviousDisabled}
                                    className="inline-flex cursor-pointer justify-center rounded-lg px-3 py-1.5 text-slate-200 bg-primary-300 focus:bg-primary-300 hover:bg-primary-700 dark:text-slate-50 transition-all duration-300 disabled:bg-slate-500 disabled:cursor-not-allowed mr-2 shadow-md">
                                    <ChevronDownIcon
                                        className="h-6 w-6 text-slate-100 dark:text-slate-200 transform rotate-180"
                                        strokeWidth="2"
                                    />
                                </button>

                                <button
                                    type="button"
                                    data-autofocus
                                    title={`${isNextDisabled ? '' : 'Next row'}`}
                                    onClick={() => changeMobileSelectedRow('next')}
                                    disabled={isNextDisabled}
                                    className="inline-flex cursor-pointer justify-center rounded-lg px-3 py-1.5 text-slate-200 bg-primary-300 focus:bg-primary-300 hover:bg-primary-700 dark:text-slate-50 transition-all duration-300 disabled:bg-slate-500 disabled:cursor-not-allowed shadow-md">
                                    <ChevronDownIcon
                                        className="h-6 w-6 text-slate-100 dark:text-slate-200"
                                        strokeWidth="2"
                                    />
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>

            <table
                className={`w-full flex flex-row flex-no-wrap justify-center rounded-lg overflow-hidden ${applyLargeBreakpoint('sm:inline-table', headingsCount)} ${applyLargeBreakpoint('sm:shadow', headingsCount)} my-5`}>
                {data?.caption && <caption>{data.caption}</caption>}
                <TableHead
                    headings={data.headings}
                    isSelectAllEnabled={isSelectAllEnabled}
                    isCheckBoxes={rows.filter((r) => r.checkbox).length > 0}
                    rowCount={rows.length}
                    onRowsSelectionToggleChange={onRowsSelectionToggleChange}
                />
                <TableBody
                    headingsCount={data.headings.length}
                    rows={rows}
                    hidden={data.hidden}
                    onSelectRow={onSelectRow}
                />
            </table>
        </>
    );
};
